/**
 * 健康调查问卷：多端附件上传（App / 微信小程序）
 */

import {
  HEALTH_SURVEY_ATTACHMENT_MAX_COUNT,
  HEALTH_SURVEY_ATTACHMENT_SCENE
} from './healthSurveyQuestions'

export { HEALTH_SURVEY_ATTACHMENT_MAX_COUNT, HEALTH_SURVEY_ATTACHMENT_SCENE }

const PENDING_UPLOAD_KEY = 'health_survey_pending_upload'
const PENDING_RESULT_KEY = 'health_survey_pending_upload_result'
const DRAFT_STORAGE_KEY = 'health_survey_draft'

/** @type {((item: string) => void)|null} */
let uploadResultHandler = null

/** stub 函数标记，用于识别是否被其他页面覆盖 */
const UPLOAD_BRIDGE_FLAG = '__healthSurveyUploadBridge__'

/** @type {Function|null} */
let uploadImageResultBridgeRef = null

/** @type {Array<{ time: string, message: string, payload?: * }>} */
const uploadDebugLogs = []

/**
 * 写入上传调试日志（console + 内存队列，VConsole 里可查看 window.__healthSurveyUploadLogs）
 * @param {string} message
 * @param {*} [payload]
 */
export function appendUploadDebugLog(message, payload) {
  const entry = {
    time: new Date().toISOString(),
    message,
    payload: payload !== undefined ? payload : null
  }
  uploadDebugLogs.push(entry)
  if (uploadDebugLogs.length > 50) {
    uploadDebugLogs.shift()
  }
  if (typeof window !== 'undefined') {
    window.__healthSurveyUploadLogs = uploadDebugLogs
  }
  if (payload !== undefined) {
    console.log(`[healthSurvey] ${message}`, payload)
  } else {
    console.log(`[healthSurvey] ${message}`)
  }
}

/**
 * 获取上传调试日志（可在 VConsole Console 输入 getUploadDebugLogs() 查看）
 * @returns {Array<{ time: string, message: string, payload?: * }>}
 */
export function getUploadDebugLogs() {
  return uploadDebugLogs.slice()
}
/**
 * 注册 App 原生上传结果处理器（优先于 sessionStorage 缓存）
 * @param {(item: string) => void|null} handler
 */
export function registerUploadImageResultHandler(handler) {
  uploadResultHandler = typeof handler === 'function' ? handler : null
}

/**
 * 判断当前 window.uploadImageResult 是否为健康问卷 stub
 * @param {*} fn
 * @returns {boolean}
 */
export function isHealthSurveyUploadBridge(fn) {
  return typeof fn === 'function' && fn[UPLOAD_BRIDGE_FLAG] === true
}

/**
 * 创建 App 原生上传回调桥接函数
 * @returns {Function}
 */
function createUploadImageResultBridge() {
  /** @param {*} item */
  const bridge = function uploadImageResultBridge(item) {
    try {
      appendUploadDebugLog('[iOS/Android回调] window.uploadImageResult 收到原生参数', item)
      appendUploadDebugLog('[iOS/Android回调] handler已注册', Boolean(uploadResultHandler))
      const text = typeof item === 'string' ? item : JSON.stringify(item)
      appendUploadDebugLog('[iOS/Android回调] 序列化后', text)
      if (uploadResultHandler) {
        uploadResultHandler(text)
        return
      }
      appendUploadDebugLog('[iOS/Android回调] handler 未就绪，写入 sessionStorage 缓存')
      sessionStorage.setItem(PENDING_RESULT_KEY, text)
    } catch (e) {
      appendUploadDebugLog('[iOS/Android回调] uploadImageResultBridge 异常', String(e))
      console.warn('[healthSurveyUpload] uploadImageResultBridge failed:', e)
    }
  }
  bridge[UPLOAD_BRIDGE_FLAG] = true
  return bridge
}

/**
 * 确保 window.uploadImageResult 指向健康问卷 stub（被其他页面覆盖或置 null 后自动恢复）
 * @returns {boolean} 是否发生了恢复
 */
export function ensureUploadImageResultStub() {
  if (typeof window === 'undefined') return false
  if (isHealthSurveyUploadBridge(window.uploadImageResult)) {
    return false
  }
  const prev = window.uploadImageResult
  uploadImageResultBridgeRef = createUploadImageResultBridge()
  window.uploadImageResult = uploadImageResultBridgeRef
  window.__healthSurveyUploadStubInstalled = true
  appendUploadDebugLog('uploadImageResult stub 已安装/恢复', {
    prevType: typeof prev,
    prevName: prev && prev.name ? prev.name : ''
  })
  return true
}

/**
 * iOS App 相册返回时 WebView 可能整页刷新，需在 Vue 挂载前缓存原生回调
 * @deprecated 请使用 ensureUploadImageResultStub
 */
export function installUploadImageResultStub() {
  ensureUploadImageResultStub()
}

/**
 * 通过 iframe 唤起原生 scheme，避免 iOS WebView 因 location.href 整页刷新
 * @param {string} url
 */
export function invokeNativeScheme(url) {
  if (typeof document === 'undefined') return
  const isIos = /(iPhone|iPad|iPod|iOS)/i.test(navigator.userAgent || '')
  if (isIos) {
    const iframe = document.createElement('iframe')
    iframe.style.cssText = 'display:none;width:0;height:0;border:0'
    iframe.src = url
    document.body.appendChild(iframe)
    window.setTimeout(() => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe)
      }
    }, 1500)
    return
  }
  window.location.href = url
}

/**
 * 标记即将离开 H5 去上传（相册/小程序上传页）
 */
export function markUploadPending() {
  try {
    sessionStorage.setItem(PENDING_UPLOAD_KEY, '1')
  } catch (e) {
    console.warn('[healthSurveyUpload] markUploadPending failed:', e)
  }
}

/**
 * 是否存在上传返回证据（草稿或已缓存的上传结果）
 * @returns {boolean}
 */
export function hasUploadReturnEvidence() {
  try {
    return Boolean(
      sessionStorage.getItem(PENDING_RESULT_KEY) ||
      sessionStorage.getItem(DRAFT_STORAGE_KEY)
    )
  } catch (e) {
    return false
  }
}

/**
 * 是否从上传流程返回
 * @returns {boolean}
 */
export function isUploadPendingReturn() {
  try {
    return sessionStorage.getItem(PENDING_UPLOAD_KEY) === '1'
  } catch (e) {
    return false
  }
}

/**
 * 清除上传返回标记
 */
export function clearUploadPendingReturn() {
  try {
    sessionStorage.removeItem(PENDING_UPLOAD_KEY)
  } catch (e) {
    console.warn('[healthSurveyUpload] clearUploadPendingReturn failed:', e)
  }
}

/**
 * 清除草稿缓存
 */
export function clearSurveyDraftStorage() {
  try {
    sessionStorage.removeItem(DRAFT_STORAGE_KEY)
  } catch (e) {
    console.warn('[healthSurveyUpload] clearSurveyDraftStorage failed:', e)
  }
}

/**
 * 读取并清除缓存的原生上传结果
 * @returns {string|null}
 */
export function consumePendingUploadResultRaw() {
  try {
    const raw = sessionStorage.getItem(PENDING_RESULT_KEY)
    if (raw) {
      sessionStorage.removeItem(PENDING_RESULT_KEY)
    }
    return raw || null
  } catch (e) {
    return null
  }
}

/**
 * 保存问卷填写草稿（相册返回整页刷新时恢复）
 * @param {Object} draft
 */
export function saveSurveyDraft(draft) {
  try {
    sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
  } catch (e) {
    console.warn('[healthSurveyUpload] saveSurveyDraft failed:', e)
  }
}

/**
 * 读取并清除问卷草稿
 * @returns {Object|null}
 */
export function loadSurveyDraft() {
  try {
    const raw = sessionStorage.getItem(DRAFT_STORAGE_KEY)
    sessionStorage.removeItem(DRAFT_STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw)
  } catch (e) {
    console.warn('[healthSurveyUpload] loadSurveyDraft failed:', e)
    return null
  }
}

/**
 * 解析原生/小程序上传回调 JSON
 * @param {string|Array|Object} item
 * @returns {Array<{ id?: string, url: string }>}
 */
export function parseUploadImageResult(item) {
  if (!item) return []
  try {
    const list = typeof item === 'string' ? JSON.parse(item) : item
    if (!Array.isArray(list)) return []
    return list
      .map((it) => ({
        id: it && it.id != null ? String(it.id) : '',
        url: it && it.url ? String(it.url) : ''
      }))
      .filter((it) => it.url)
  } catch (e) {
    console.warn('[healthSurveyUpload] parseUploadImageResult failed:', e)
    return []
  }
}

/**
 * App 内嵌 H5：唤起原生相册/相机上传
 * @param {number} maxCount
 */
export function openNativeImageUpload(maxCount) {
  ensureUploadImageResultStub()
  const mc = Number.isFinite(Number(maxCount))
    ? Math.max(1, Math.min(HEALTH_SURVEY_ATTACHMENT_MAX_COUNT, Number(maxCount)))
    : HEALTH_SURVEY_ATTACHMENT_MAX_COUNT
  markUploadPending()
  const url = `appdjxhealth://actionUpdateImage?maxCount=${mc}`
  appendUploadDebugLog('唤起原生相册', { url, uploadImageResultType: typeof (window && window.uploadImageResult) })
  invokeNativeScheme(url)
}

/**
 * 微信小程序 web-view：跳转小程序上传页
 * @param {Object} options
 * @param {number} options.maxCount
 * @param {string} [options.hashPart]
 */
export function openMiniProgramImageUpload(options = {}) {
  const maxCount = Number.isFinite(Number(options.maxCount))
    ? Math.max(1, Math.min(HEALTH_SURVEY_ATTACHMENT_MAX_COUNT, Number(maxCount)))
    : HEALTH_SURVEY_ATTACHMENT_MAX_COUNT
  if (typeof wx === 'undefined' || !wx.miniProgram || typeof wx.miniProgram.navigateTo !== 'function') {
    throw new Error('请在微信小程序中打开')
  }
  markUploadPending()
  const token = encodeURIComponent(String(localStorage.getItem('token') || ''))
  const baseH5 = encodeURIComponent(String((window.location.origin || '') + (window.location.pathname || '')))
  const hashPart = encodeURIComponent(String(options.hashPart || '#/healthSurvey'))
  const year = encodeURIComponent(String(new Date().getFullYear()))
  const type = encodeURIComponent(String(HEALTH_SURVEY_ATTACHMENT_SCENE))
  wx.miniProgram.navigateTo({
    url: `/pages/upload/index?year=${year}&type=${type}&maxCount=${maxCount}&token=${token}&baseH5=${baseH5}&hashPart=${hashPart}`
  })
}

/**
 * 合并上传结果到已有列表（按 url 去重）
 * @param {Array<{ id?: string, url: string }>} current
 * @param {Array<{ id?: string, url: string }>} incoming
 * @param {number} [maxCount]
 * @returns {Array<{ id?: string, url: string }>}
 */
export function mergeAttachmentFiles(current, incoming, maxCount = HEALTH_SURVEY_ATTACHMENT_MAX_COUNT) {
  const next = Array.isArray(current) ? current.slice() : []
  const urlSet = new Set(next.map((f) => f.url))
  ;(incoming || []).forEach((file) => {
    if (!file || !file.url || urlSet.has(file.url)) return
    if (next.length >= maxCount) return
    next.push({ id: file.id || '', url: file.url })
    urlSet.add(file.url)
  })
  return next
}



// WEBPACK FOOTER //
// ./src/views/mine/healthSurvey/healthSurveyUpload.js